/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  Component,
  ComponentRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import {getListPageComponent} from './route-page-component-registry';
import {AdnResourceListService} from './adn-resource-list.service';
import {columnFinder, getListChildren} from './resource-list-utils';
import {AdnListHelperService} from './list-helper.service';
import {TimezonesResource} from '../../../components/api/resources/resource/timezones';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {AdnTimeShifterService} from '../../../components/util/adn-time-shifter.service';
import {SiteResource} from '../../../components/api/resources/resource/site.resource';
import {LineItemResource} from '../../../components/api/resources/resource/line-item.resource';
import {CreativeResource} from '../../../components/api/resources/resource/creative.resource';

@Component({
  standalone: false,
  selector: 'adn-resource-list-route-page',
  template: '<ng-template #host></ng-template>'
})
export class ResourceListRoutePageComponent implements OnInit, OnDestroy {
  @Input() listPage: string;

  @ViewChild('host', {read: ViewContainerRef, static: true}) host: ViewContainerRef;

  list: any;
  private componentRef: ComponentRef<any>;

  constructor(
    private readonly uiRouter: UIRouter,
    private readonly $state: StateService,
    private readonly listService: AdnResourceListService,
    private readonly adnListHelper: AdnListHelperService,
    private readonly localNetworkProfile: LocalNetworkProfile,
    private readonly localUserPermissions: LocalUserPermissions,
    private readonly localUserProfile: LocalUserProfile,
    private readonly adnTimeShifter: AdnTimeShifterService,
    private readonly timezonesResource: TimezonesResource,
    private readonly siteResource: SiteResource,
    private readonly lineItemResource: LineItemResource,
    private readonly creativeResource: CreativeResource
  ) {
  }

  ngOnInit(): void {
    const listComponent = getListPageComponent(this.listPage);
    if (!listComponent) {
      return;
    }

    const locals = this.buildControllerLocals();
    this.list = this.buildList(locals);

    this.componentRef = this.host.createComponent(listComponent);
    this.componentRef.setInput('list', this.list);
  }

  ngOnDestroy(): void {
    this.componentRef?.destroy();
  }

  private buildControllerLocals(): Record<string, any> {
    const transition = this.uiRouter.globals.successfulTransitions.peekTail();
    const transitionInjector = transition?.injector();
    const resolve = (key: string) => {
      try {
        return transitionInjector?.get(key);
      } catch (e) {
        return undefined;
      }
    };

    return {
      ResourceFactory: resolve('ResourceFactory'),
      ChildResourceFactory: resolve('ChildResourceFactory'),
      orderByParam: resolve('orderByParam'),
      listConfig: resolve('listConfig'),
      objectType: resolve('objectType'),
      extension: resolve('extension'),
      listExtension: resolve('listExtension'),
      bulk: resolve('bulk'),
      readOnly: resolve('readOnly')
    };
  }

  private buildList(locals: Record<string, any>): any {
    const list: any = {};
    if (locals.bulk) {
      _.forEach(locals.bulk, (value: any, key: string) => {
        list[key] = value(list, {
          $state: this.$state
        }, {
          TimeZones: this.timezonesResource,
          Site: this.siteResource,
          LineItem: this.lineItemResource,
          Creative: this.creativeResource
        }, {
          LocalNetworkProfile: this.localNetworkProfile,
          LocalUserPermissions: this.localUserPermissions,
          adnTimeShifter: this.adnTimeShifter,
          localUserProfile: this.localUserProfile
        });
      });
    }

    list.columnFinder = (column: string, param: string) => columnFinder(list, column, param);

    _.forEach(locals.listExtension, (value: any, key: string) => {
      if (_.isFunction(value)) {
        list[key] = value;
      }
    });

    const extensionHandler = (afterFill?: boolean) => {
      if (locals.extension && _.isFunction(locals.extension.func)) {
        locals.extension.func(list, afterFill);
      }
    };

    const config = _.assign({
      getDefaultOrderByParam: () => locals.orderByParam,
      baseTable: locals.objectType.apiParam,
      afterFill: () => extensionHandler(true)
    }, locals.listConfig || {});

    this.listService.createList(
      list,
      locals.ResourceFactory,
      locals.readOnly,
      'full' + (_.get(locals.listConfig, 'listIdOverride') || locals.objectType.type) + 'List',
      config
    );
    list.canExpand = !!locals.ChildResourceFactory;
    list.perms = this.localUserPermissions.getAllVisPermissions();
    getListChildren(list, this.adnListHelper, locals.objectType, locals.ChildResourceFactory);

    extensionHandler();
    return list;
  }
}
